import React, { useState, useEffect } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import { useAlertModal } from '../../AlertModalContext';

/* ── ícone reutilizável ── */
const Icon = ({ d, size = 16, color = 'currentColor', stroke = 2 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
  </svg>
);

const ICONS = {
  chevron:  'M19 9l-7 7-7-7',
  filter:   'M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z',
  export:   'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
  eye:      'M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 0c0 5-4.03 9-9 9S3 17 3 12 7.03 3 12 3s9 4 9 9z',
  plus:     'M12 4v16m8-8H4',
  check:    'M5 13l4 4L19 7',
  back:     'M10 19l-7-7m0 0l7-7m-7 7h18',
  chart:    'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v16H4z',
  user:     'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  empty:    'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
};

/* ── helpers de estilo ── */
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151',
  fontFamily: "'Inter','Segoe UI',sans-serif", outline: 'none',
  backgroundColor: '#fff', boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const focusHandlers = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};
const FieldLabel = ({ children }) => (
  <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>
    {children}
  </label>
);

/* ── accordion nativo ── */
const AccordionSection = ({ title, icon, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '10px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', border: 'none', cursor: 'pointer',
          background: open ? 'linear-gradient(135deg, #eff6ff, #f8fafc)' : '#f9fafb',
          fontFamily: 'inherit', transition: 'background 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <Icon d={icon} size={14} color="#2563eb" />
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>{title}</span>
        </div>
        <div style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          <Icon d={ICONS.chevron} size={14} color="#6b7280" />
        </div>
      </button>
      {open && (
        <div style={{ padding: '18px 20px', backgroundColor: '#fff', borderTop: '1px solid #f1f5f9' }}>
          {children}
        </div>
      )}
    </div>
  );
};

/* ── barra de filtros ── */
const FilterBar = ({ children, onFilter, isLoading, onExport }) => (
  <div style={{
    display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end',
    marginBottom: '16px', padding: '14px 16px',
    backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9',
  }}>
    {children}
    <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
      {onFilter && (
        <button
          type="button" onClick={onFilter} disabled={isLoading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: isLoading ? '#93c5fd' : 'linear-gradient(135deg, #1e3a5f, #2563eb)',
            color: '#fff', fontSize: '12px', fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}
        >
          {isLoading
            ? <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
            : <Icon d={ICONS.filter} size={11} color="#fff" />
          }
          {isLoading ? 'Filtrando...' : 'Filtrar'}
        </button>
      )}
      {onExport && (
        <button
          type="button" onClick={onExport}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', borderRadius: '8px',
            border: '1px solid #e5e7eb', backgroundColor: '#fff',
            color: '#374151', fontSize: '12px', fontWeight: '600',
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <Icon d={ICONS.export} size={11} color="#374151" />
          CSV
        </button>
      )}
    </div>
  </div>
);

/* ── tabela nativa ── */
const DataTable = ({ headers, children, emptyMessage = 'Nenhum dado encontrado.' }) => (
  React.Children.count(children) === 0 ? (
    <div style={{ padding: '40px 24px', textAlign: 'center' }}>
      <Icon d={ICONS.empty} size={28} color="#d1d5db" />
      <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#9ca3af', fontWeight: '500' }}>{emptyMessage}</p>
    </div>
  ) : (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                padding: '9px 13px', fontSize: '11px', fontWeight: '700',
                color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px',
                borderBottom: '2px solid #e2e8f0', background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)',
                textAlign: h.align || 'left', whiteSpace: 'nowrap',
                ...(h.hideXs ? { display: 'none' } : {}),
              }}
                className={h.hideXs ? 'hide-xs' : h.hideSm ? 'hide-sm' : ''}
              >
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
);

const Td = ({ children, align = 'left', muted, mono, className }) => (
  <td
    className={className}
    style={{
      padding: '10px 13px', fontSize: '13px', borderBottom: '1px solid #f1f5f9',
      color: muted ? '#6b7280' : '#374151', textAlign: align,
      fontFamily: mono ? 'monospace' : 'inherit', verticalAlign: 'middle',
    }}
  >
    {children}
  </td>
);

/* ════════════════════════════════════════════════════════ */

const RelatoriosFaturamentoTab = ({ currentEntidade, formatCurrency, isActive }) => {
  const { showAlertModal } = useAlertModal();

  /* ── estados: faturamento por serviço ── */
  const [faturamentoPorServicoData,    setFaturamentoPorServicoData]    = useState([]);
  const [isLoadingFaturamentoServico,  setIsLoadingFaturamentoServico]  = useState(false);
  const [filtroDataInicioServico,      setFiltroDataInicioServico]      = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [filtroDataFimServico,         setFiltroDataFimServico]         = useState(new Date().toISOString().split('T')[0]);

  /* ── estados: faturamento por paciente ── */
  const [faturamentoPorPacienteData,   setFaturamentoPorPacienteData]   = useState([]);
  const [isLoadingFaturamentoPaciente, setIsLoadingFaturamentoPaciente] = useState(false);
  const [filtroDataInicioPaciente,     setFiltroDataInicioPaciente]     = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [filtroDataFimPaciente,        setFiltroDataFimPaciente]        = useState(new Date().toISOString().split('T')[0]);

  /* ── estados: faturamento por período ── */
  const [faturamentoPorPeriodoData,    setFaturamentoPorPeriodoData]    = useState({ labels: [], datasets: [] });
  const [isLoadingFaturamentoPeriodo,  setIsLoadingFaturamentoPeriodo]  = useState(false);
  const [filtroTipoPeriodo,            setFiltroTipoPeriodo]            = useState('mensal');
  const [filtroAnoPeriodo,             setFiltroAnoPeriodo]             = useState(new Date().getFullYear());
  const [filtroDataInicioPeriodo,      setFiltroDataInicioPeriodo]      = useState(new Date(new Date().setDate(new Date().getDate() - 29)).toISOString().split('T')[0]);
  const [filtroDataFimPeriodo,         setFiltroDataFimPeriodo]         = useState(new Date().toISOString().split('T')[0]);

  /* ── estados: modal de detalhes do paciente ── */
  const [showDetalhesModal,            setShowDetalhesModal]            = useState(false);
  const [selectedPacienteDetalhes,     setSelectedPacienteDetalhes]     = useState(null);
  const [detalhesPacienteData,         setDetalhesPacienteData]         = useState([]);
  const [isLoadingDetalhesPaciente,    setIsLoadingDetalhesPaciente]    = useState(false);

  /* ── estados: comissão ── */
  const [allFuncionarios,              setAllFuncionarios]              = useState([]);
  const [allServices,                  setAllServices]                  = useState([]);
  const [editingCommission,            setEditingCommission]            = useState({ pagamentoId: null, servicoId: null });
  const [selectedProfessional,         setSelectedProfessional]         = useState('');
  const [commissionPercentage,         setCommissionPercentage]         = useState('');
  const [isAddingCommission,           setIsAddingCommission]           = useState(false);

  /* ── dados auxiliares ── */
  useEffect(() => {
    if (!currentEntidade?.id) return;
    axios.get(`/api/servicos?entidade_id=${currentEntidade.id}`)
      .then(r => setAllServices(r.data.servicos || [])).catch(console.error);
    axios.get('/api/relatorios/funcionarios-comissionaveis', { params: { entidade_id: currentEntidade.id } })
      .then(r => setAllFuncionarios(r.data.funcionarios || [])).catch(console.error);
  }, [currentEntidade]);

  /* ── comissão ── */
  const handleStartAddCommission = (pagamentoId, servicoId) => {
    setEditingCommission({ pagamentoId, servicoId });
    setSelectedProfessional('');
    const svc = allServices.find(s => s.id === servicoId);
    setCommissionPercentage(svc?.percentual_comissao || 0);
  };
  const handleCancelAddCommission = () => {
    setEditingCommission({ pagamentoId: null, servicoId: null });
    setSelectedProfessional(''); setCommissionPercentage('');
  };
  const handleSaveCommission = async (pagamentoId, servicoId) => {
    if (!selectedProfessional) { showAlertModal('Atenção', 'Selecione um profissional.', 'warning'); return; }
    setIsAddingCommission(true);
    try {
      await axios.post(`/api/pagamentos/${pagamentoId}/servicos/${servicoId}/comissao`, {
        funcionario_id: selectedProfessional, percentual_comissao: commissionPercentage,
      });
      showAlertModal('Sucesso', 'Comissão atribu', 'success');
      handleCancelAddCommission();
      if (selectedPacienteDetalhes) fetchDetalhesPaciente(selectedPacienteDetalhes.paciente_id);
    } catch (err) {
      showAlertModal('Erro', `Falha ao adicionar comissão: ${err.response?.data?.message || err.message}`, 'danger');
    } finally { setIsAddingCommission(false); }
  };

  /* ── fetches ── */
  const fetchFaturamentoPorServico = async () => {
    if (!filtroDataInicioServico || !filtroDataFimServico || !currentEntidade) return;
    setIsLoadingFaturamentoServico(true);
    try {
      const r = await axios.get('/api/relatorios/faturamento/por-servico', {
        params: { entidade_id: currentEntidade.id, data_inicio: filtroDataInicioServico, data_fim: filtroDataFimServico },
      });
      setFaturamentoPorServicoData(r.data.dados || []);
    } catch { setFaturamentoPorServicoData([]); }
    finally { setIsLoadingFaturamentoServico(false); }
  };

  const fetchFaturamentoPorPaciente = async () => {
    if (!filtroDataInicioPaciente || !filtroDataFimPaciente || !currentEntidade) return;
    setIsLoadingFaturamentoPaciente(true);
    try {
      const r = await axios.get('/api/relatorios/faturamento/por-paciente', {
        params: { entidade_id: currentEntidade.id, data_inicio: filtroDataInicioPaciente, data_fim: filtroDataFimPaciente },
      });
      setFaturamentoPorPacienteData(r.data.dados || []);
    } catch { setFaturamentoPorPacienteData([]); }
    finally { setIsLoadingFaturamentoPaciente(false); }
  };

  const fetchFaturamentoPorPeriodo = async () => {
    if (!currentEntidade) return;
    setIsLoadingFaturamentoPeriodo(true);
    const params = { entidade_id: currentEntidade.id, tipo_periodo: filtroTipoPeriodo };
    if (filtroTipoPeriodo === 'mensal' || filtroTipoPeriodo === 'anual') params.ano = filtroAnoPeriodo;
    else { params.data_inicio = filtroDataInicioPeriodo; params.data_fim = filtroDataFimPeriodo; }
    try {
      const r = await axios.get('/api/relatorios/faturamento/por-periodo', { params });
      setFaturamentoPorPeriodoData({
        labels: r.data.labels || [],
        datasets: [{
          label: `Faturamento ${filtroTipoPeriodo.charAt(0).toUpperCase() + filtroTipoPeriodo.slice(1)} (R$)`,
          data: r.data.data || [],
          borderColor: 'rgb(37,99,235)', backgroundColor: 'rgba(37,99,235,0.15)',
          tension: 0.4, pointRadius: 4,
        }],
      });
    } catch { setFaturamentoPorPeriodoData({ labels: [], datasets: [] }); }
    finally { setIsLoadingFaturamentoPeriodo(false); }
  };

  const fetchDetalhesPaciente = async (pacienteId) => {
    if (!pacienteId || !currentEntidade) return;
    setIsLoadingDetalhesPaciente(true);
    try {
      const r = await axios.get('/api/relatorios/faturamento/paciente-detalhes', {
        params: { entidade_id: currentEntidade.id, paciente_id: pacienteId, data_inicio: filtroDataInicioPaciente, data_fim: filtroDataFimPaciente },
      });
      setDetalhesPacienteData(r.data.dados || []);
    } catch { setDetalhesPacienteData([]); }
    finally { setIsLoadingDetalhesPaciente(false); }
  };

  useEffect(() => {
    if (isActive && currentEntidade) {
      fetchFaturamentoPorServico();
      fetchFaturamentoPorPaciente();
      fetchFaturamentoPorPeriodo();
    }
  }, [isActive, currentEntidade, filtroDataInicioServico, filtroDataFimServico,
      filtroDataInicioPaciente, filtroDataFimPaciente, filtroTipoPeriodo,
      filtroAnoPeriodo, filtroDataInicioPeriodo, filtroDataFimPeriodo]);

  useEffect(() => {
    if (selectedPacienteDetalhes) fetchDetalhesPaciente(selectedPacienteDetalhes.paciente_id);
  }, [selectedPacienteDetalhes]);

  /* ── RENDER ── */
  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .fat-tr:hover { background: #fafbff; }
        @media (max-width:575px) { .hide-xs { display:none!important; } }
        @media (max-width:767px) { .hide-sm { display:none!important; } }
      `}</style>

      {/* ══ Faturamento por Serviço ══ */}
      <AccordionSection title="Faturamento Detalhado por Serviço" icon={ICONS.chart} defaultOpen>
        <FilterBar
          onFilter={fetchFaturamentoPorServico}
          isLoading={isLoadingFaturamentoServico}
          onExport={() => {}}
        >
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Data Início</FieldLabel>
            <input style={inputStyle} type="date" value={filtroDataInicioServico} onChange={e => setFiltroDataInicioServico(e.target.value)} {...focusHandlers} />
          </div>
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Data Fim</FieldLabel>
            <input style={inputStyle} type="date" value={filtroDataFimServico} onChange={e => setFiltroDataFimServico(e.target.value)} {...focusHandlers} />
          </div>
        </FilterBar>

        {isLoadingFaturamentoServico ? (
          <LoadingRow />
        ) : (
          <DataTable
            headers={[
              { label: 'Serviço' },
              { label: 'Qtd.', hideSm: true },
              { label: 'Valor Unit. Médio', hideSm: true },
              { label: 'Faturamento Total', align: 'right' },
              { label: '% Total', align: 'right' },
            ]}
            emptyMessage="Nenhum dado de faturamento por serviço para o período."
          >
            {faturamentoPorServicoData.map(item => (
              <tr key={item.id || item.nome_servico} className="fat-tr">
                <Td><span style={{ fontWeight: '600' }}>{item.nome_servico}</span></Td>
                <Td className="hide-sm" muted>{item.quantidade_realizado}</Td>
                <Td className="hide-sm" muted>{formatCurrency(item.valor_unitario_medio)}</Td>
                <Td align="right"><span style={{ fontWeight: '700', color: '#16a34a' }}>{formatCurrency(item.faturamento_total_servico)}</span></Td>
                <Td align="right">
                  <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 7px', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                    {item.percentual_faturamento_total.toFixed(2)}%
                  </span>
                </Td>
              </tr>
            ))}
          </DataTable>
        )}
      </AccordionSection>

      {/* ══ Faturamento por Paciente ══ */}
      <AccordionSection title="Faturamento por Paciente" icon={ICONS.user}>
        <FilterBar
          onFilter={fetchFaturamentoPorPaciente}
          isLoading={isLoadingFaturamentoPaciente}
          onExport={() => {}}
        >
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Data Início</FieldLabel>
            <input style={inputStyle} type="date" value={filtroDataInicioPaciente} onChange={e => setFiltroDataInicioPaciente(e.target.value)} {...focusHandlers} />
          </div>
          <div style={{ minWidth: '150px' }}>
            <FieldLabel>Data Fim</FieldLabel>
            <input style={inputStyle} type="date" value={filtroDataFimPaciente} onChange={e => setFiltroDataFimPaciente(e.target.value)} {...focusHandlers} />
          </div>
        </FilterBar>

        {isLoadingFaturamentoPaciente ? (
          <LoadingRow />
        ) : (
          <DataTable
            headers={[
              { label: 'Paciente' },
              { label: 'Nº Serviços', align: 'center' },
              { label: 'Faturamento Total', align: 'right' },
              { label: 'Último Serviço', hideSm: true },
              { label: 'Ações', align: 'center' },
            ]}
            emptyMessage="Nenhum dado de faturamento por paciente para o período."
          >
            {faturamentoPorPacienteData.map(item => (
              <tr key={item.id || item.nome_paciente} className="fat-tr">
                <Td><span style={{ fontWeight: '600' }}>{item.nome_paciente}</span></Td>
                <Td align="center" muted>{item.numero_servicos_realizados}</Td>
                <Td align="right"><span style={{ fontWeight: '700', color: '#16a34a' }}>{formatCurrency(item.faturamento_total_paciente)}</span></Td>
                <Td className="hide-sm" muted>{new Date(item.data_ultimo_servico + 'T00:00:00').toLocaleDateString('pt-BR')}</Td>
                <Td align="center">
                  <button
                    onClick={() => { setSelectedPacienteDetalhes(item); setShowDetalhesModal(true); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#2563eb', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    <Icon d={ICONS.eye} size={11} color="#2563eb" /> Detalhes
                  </button>
                </Td>
              </tr>
            ))}
          </DataTable>
        )}
      </AccordionSection>

      {/* ══ Faturamento por Período ══ */}
      <AccordionSection title="Faturamento por Período" icon={ICONS.chart}>
        <FilterBar onFilter={fetchFaturamentoPorPeriodo} isLoading={isLoadingFaturamentoPeriodo}>
          <div style={{ minWidth: '180px' }}>
            <FieldLabel>Agrupar por</FieldLabel>
            <select style={{ ...inputStyle, appearance: 'auto' }} value={filtroTipoPeriodo} onChange={e => setFiltroTipoPeriodo(e.target.value)} {...focusHandlers}>
              <option value="diario">Diário (Últimos 30 dias)</option>
              <option value="semanal">Semanal (Últimas 12 semanas)</option>
              <option value="mensal">Mensal (Ano Corrente)</option>
              <option value="anual">Anual</option>
            </select>
          </div>
          {(filtroTipoPeriodo === 'mensal' || filtroTipoPeriodo === 'anual') && (
            <div style={{ minWidth: '100px' }}>
              <FieldLabel>Ano</FieldLabel>
              <input style={inputStyle} type="number" value={filtroAnoPeriodo} onChange={e => setFiltroAnoPeriodo(parseInt(e.target.value))} {...focusHandlers} />
            </div>
          )}
          {(filtroTipoPeriodo === 'diario' || filtroTipoPeriodo === 'semanal') && (
            <>
              <div style={{ minWidth: '150px' }}>
                <FieldLabel>Data Início</FieldLabel>
                <input style={inputStyle} type="date" value={filtroDataInicioPeriodo} onChange={e => setFiltroDataInicioPeriodo(e.target.value)} {...focusHandlers} />
              </div>
              <div style={{ minWidth: '150px' }}>
                <FieldLabel>Data Fim</FieldLabel>
                <input style={inputStyle} type="date" value={filtroDataFimPeriodo} onChange={e => setFiltroDataFimPeriodo(e.target.value)} {...focusHandlers} />
              </div>
            </>
          )}
        </FilterBar>

        {isLoadingFaturamentoPeriodo ? (
          <LoadingRow />
        ) : faturamentoPorPeriodoData.labels.length > 0 ? (
          <div style={{ backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #f1f5f9', padding: '16px' }}>
            <div style={{ height: '280px' }}>
              <Line
                data={faturamentoPorPeriodoData}
                options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    y: { beginAtZero: true, ticks: { callback: v => formatCurrency(v), font: { size: 10 } }, grid: { color: '#f1f5f9' } },
                    x: { ticks: { font: { size: 10 } }, grid: { display: false } },
                  },
                }}
              />
            </div>
          </div>
        ) : (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <Icon d={ICONS.chart} size={28} color="#d1d5db" />
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#9ca3af' }}>Selecione os filtros para gerar o gráfico.</p>
          </div>
        )}
      </AccordionSection>

      {/* ══ MODAL: Detalhes do Paciente ══ */}
      <Modal show={showDetalhesModal} onHide={() => setShowDetalhesModal(false)} size="lg" centered>
        <Modal.Header style={{ padding: 0, border: 'none' }}>
          <div style={{
            width: '100%', padding: '18px 22px',
            background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '9px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon d={ICONS.user} size={16} color="#fff" />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#fff' }}>
                Detalhes de Faturamento
              </p>
              <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
                {selectedPacienteDetalhes?.nome_paciente}
              </p>
            </div>
            <button onClick={() => setShowDetalhesModal(false)} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>×</button>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
          {isLoadingDetalhesPaciente ? (
            <LoadingRow />
          ) : detalhesPacienteData.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <Icon d={ICONS.empty} size={28} color="#d1d5db" />
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#9ca3af' }}>Nenhum detalhe encontrado para este paciente no período.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                <thead>
                  <tr>
                    {['Data', 'Serviço', 'Valor Pago', '% Comissão', 'Recebido por', 'Ações'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '2px solid #e2e8f0', background: '#f8fafc', textAlign: 'left', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detalhesPacienteData.map((detalhe, idx) => {
                    const svcDef         = allServices.find(s => s.id === detalhe.servico_id || s.nome === detalhe.nome_servico);
                    const isCommissionable = svcDef && parseFloat(svcDef.percentual_comissao) > 0;
                    const hasCommission  = !!detalhe.nome_comissionado;
                    const pctExibido     = detalhe.percentual_comissao_pago || detalhe.percentual_comissao;
                    const isEditing      = editingCommission.pagamentoId === detalhe.pagamento_id && editingCommission.servicoId === detalhe.servico_id;

                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: '#6b7280', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {new Date(detalhe.data_servico + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                          {detalhe.nome_servico}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '700', color: '#16a34a' }}>
                          {formatCurrency(detalhe.valor_pago)}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '12px', color: '#374151' }}>
                          {pctExibido
                            ? <span style={{ fontWeight: '700' }}>{parseFloat(pctExibido).toFixed(2)}%</span>
                            : isCommissionable
                              ? <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '11px' }}>{parseFloat(svcDef.percentual_comissao).toFixed(2)}% (elegível)</span>
                              : <span style={{ color: '#d1d5db' }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#374151' }}>
                          {detalhe.nome_comissionado || <span style={{ color: '#d1d5db' }}>—</span>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {!hasCommission && isCommissionable && (
                            isEditing ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <select
                                  style={{ ...inputStyle, maxWidth: '140px', padding: '5px 8px', appearance: 'auto' }}
                                  value={selectedProfessional}
                                  onChange={e => setSelectedProfessional(e.target.value)}
                                  {...focusHandlers}
                                >
                                  <option value="">Selecione...</option>
                                  {allFuncionarios.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                                </select>
                                <input
                                  style={{ ...inputStyle, maxWidth: '60px', padding: '5px 8px' }}
                                  type="number" placeholder="%" value={commissionPercentage}
                                  onChange={e => setCommissionPercentage(e.target.value)}
                                  {...focusHandlers}
                                />
                                <button
                                  onClick={() => handleSaveCommission(detalhe.pagamento_id, detalhe.servico_id)}
                                  disabled={isAddingCommission}
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', border: 'none', backgroundColor: '#16a34a', color: '#fff', cursor: 'pointer' }}
                                >
                                  {isAddingCommission
                                    ? <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                    : <Icon d={ICONS.check} size={12} color="#fff" stroke={2.5} />
                                  }
                                </button>
                                <button
                                  onClick={handleCancelAddCommission}
                                  disabled={isAddingCommission}
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer' }}
                                >
                                  <Icon d={ICONS.back} size={12} color="#374151" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleStartAddCommission(detalhe.pagamento_id, detalhe.servico_id)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#2563eb', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                                title="Adicionar Comissão"
                              >
                                <Icon d={ICONS.plus} size={11} color="#2563eb" /> Comissão
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer style={{ padding: '12px 20px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowDetalhesModal(false)} style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' }}>
            Fechar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

/* ── spinner de linha ── */
const LoadingRow = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', gap: '10px' }}>
    <Spinner animation="border" variant="primary" style={{ width: '24px', height: '24px' }} />
    <span style={{ fontSize: '13px', color: '#6b7280' }}>Carregando...</span>
  </div>
);

export default RelatoriosFaturamentoTab;
